import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Video, Calendar, Clock, Users, Plus, Copy, ExternalLink,
  Play, Square, Trash2, Edit, FileText, CheckCircle, 
  Filter, Search, RefreshCw, Mic, MicOff, Camera, CameraOff,
  Link2, Mail, Bell, BookOpen, Shield, ClipboardList, AlertTriangle
} from 'lucide-react';
import PageHeader from './ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  safety_training: { label: 'Safety Training', icon: BookOpen, color: 'bg-blue-100 text-blue-700', gradient: 'from-blue-500 to-blue-600' },
  toolbox_talk: { label: 'Toolbox Talk', icon: ClipboardList, color: 'bg-orange-100 text-orange-700', gradient: 'from-orange-500 to-orange-600' },
  safety_meeting: { label: 'Safety Meeting', icon: Shield, color: 'bg-green-100 text-green-700', gradient: 'from-green-500 to-green-600' },
  incident_review: { label: 'Incident Review', icon: AlertTriangle, color: 'bg-red-100 text-red-700', gradient: 'from-red-500 to-red-600' },
  audit_review: { label: 'Audit Review', icon: FileText, color: 'bg-purple-100 text-purple-700', gradient: 'from-purple-500 to-purple-600' },
  general: { label: 'General', icon: Video, color: 'bg-gray-100 text-gray-700', gradient: 'from-gray-500 to-gray-600' }
};

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const MeetingsPage = () => {
  const [meetings, setMeetings] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [copiedLink, setCopiedLink] = useState(null);

  // Form state
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    category: 'general',
    meeting_type: 'scheduled',
    scheduled_start: '',
    scheduled_end: '',
    attendee_emails: ''
  });

  // Notes state
  const [meetingNotes, setMeetingNotes] = useState({
    notes: '',
    action_items: '',
    decisions: ''
  });

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      const [meetingsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/meetings`, { headers }),
        axios.get(`${API_URL}/api/meetings/stats/summary`, { headers })
      ]);
      setMeetings(meetingsRes.data.meetings || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleCreateMeeting = async (isInstant = false) => {
    try {
      if (isInstant) {
        const response = await axios.post(`${API_URL}/api/meetings/instant`, {
          title: `Instant Meeting - ${new Date().toLocaleString()}`,
          category: 'general'
        }, { headers });
        
        // Open meet link in new tab
        window.open(response.data.meet_link, '_blank');
        fetchMeetings();
        return;
      }

      const payload = {
        ...newMeeting,
        attendee_emails: newMeeting.attendee_emails.split(',').map(e => e.trim()).filter(Boolean)
      };

      await axios.post(`${API_URL}/api/meetings`, payload, { headers });
      setShowCreateModal(false);
      setNewMeeting({
        title: '',
        description: '',
        category: 'general',
        meeting_type: 'scheduled',
        scheduled_start: '',
        scheduled_end: '',
        attendee_emails: ''
      });
      fetchMeetings();
    } catch (error) {
      console.error('Error creating meeting:', error);
    }
  };

  const handleEndMeeting = async (meetingId) => {
    try {
      await axios.post(`${API_URL}/api/meetings/${meetingId}/end`, {}, { headers });
      fetchMeetings();
    } catch (error) {
      console.error('Error ending meeting:', error);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;
    try {
      await axios.delete(`${API_URL}/api/meetings/${meetingId}`, { headers });
      fetchMeetings();
    } catch (error) {
      console.error('Error deleting meeting:', error);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedMeeting) return;
    try {
      await axios.post(`${API_URL}/api/meetings/${selectedMeeting.id}/notes`, {
        notes: meetingNotes.notes,
        action_items: meetingNotes.action_items.split('\n').filter(Boolean),
        decisions: meetingNotes.decisions.split('\n').filter(Boolean)
      }, { headers });
      setShowNotesModal(false);
      setMeetingNotes({ notes: '', action_items: '', decisions: '' });
      fetchMeetings();
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const copyMeetingLink = (link, meetingId) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(meetingId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const joinMeeting = (link) => {
    window.open(link, '_blank');
  };

  // Filter meetings
  const filteredMeetings = meetings.filter(meeting => {
    if (filterCategory !== 'all' && meeting.category !== filterCategory) return false;
    if (filterStatus !== 'all' && meeting.status !== filterStatus) return false;
    if (searchQuery && !meeting.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const upcomingMeetings = meetings.filter(m => m.status === 'scheduled' || m.status === 'in_progress').slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Unified Page Header */}
      <div className="mx-4 md:mx-6 mt-4 md:mt-6">
        <PageHeader
          title="Safety Meetings"
          subtitle="Schedule and manage virtual safety meetings with Google Meet"
          icon={Video}
          variant="purple"
          onRefresh={fetchMeetings}
          loading={loading}
          actions={
            <div className="flex gap-2">
              <Button 
                onClick={() => handleCreateMeeting(true)}
                size="sm"
                className="bg-green-500/80 hover:bg-green-500 text-white border-0"
              >
                <Play className="w-4 h-4 mr-2" />
                Instant
              </Button>
              <Button 
                onClick={() => setShowCreateModal(true)}
                size="sm"
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule
              </Button>
            </div>
          }
          stats={[
            { icon: Video, label: 'Total Meetings', value: stats.total || 0, color: 'purple' },
            { icon: Calendar, label: 'Scheduled', value: stats.scheduled || 0, color: 'blue' },
            { icon: Play, label: 'In Progress', value: stats.in_progress || 0, color: 'green' },
            { icon: Clock, label: 'This Month', value: stats.this_month || 0, color: 'cyan' }
          ]}
        />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
        {/* Upcoming Meetings Banner */}
        {upcomingMeetings.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-green-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <Bell className="w-5 h-5 text-green-500" />
                Upcoming Meetings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {upcomingMeetings.map(meeting => {
                  const category = CATEGORY_CONFIG[meeting.category] || CATEGORY_CONFIG.general;
                  return (
                    <div key={meeting.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <category.icon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium">{meeting.title}</span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={() => joinMeeting(meeting.meet_link)}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Join
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search meetings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchMeetings}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Meetings Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <Card className="text-center py-12">
            <Video className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No meetings found</h3>
            <p className="text-gray-500 mb-4">Schedule your first safety meeting to get started</p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Schedule Meeting
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMeetings.map(meeting => {
              const category = CATEGORY_CONFIG[meeting.category] || CATEGORY_CONFIG.general;
              const status = STATUS_CONFIG[meeting.status] || STATUS_CONFIG.scheduled;
              const CategoryIcon = category.icon;

              return (
                <Card key={meeting.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center`}>
                        <CategoryIcon className="w-5 h-5 text-white" />
                      </div>
                      <Badge className={status.color}>{status.label}</Badge>
                    </div>
                    <CardTitle className="text-base md:text-lg mt-3">{meeting.title}</CardTitle>
                    {meeting.description && (
                      <CardDescription className="text-sm line-clamp-2">{meeting.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-gray-600">
                      {meeting.scheduled_start && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(meeting.scheduled_start).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>{meeting.attendee_emails?.length || 0} attendees</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        <span className="truncate text-xs">{meeting.meet_link}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                      <Button 
                        size="sm" 
                        onClick={() => joinMeeting(meeting.meet_link)}
                        className="flex-1"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Join
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => copyMeetingLink(meeting.meet_link, meeting.id)}
                      >
                        {copiedLink === meeting.id ? (
                          <CheckCircle className="w-3 h-3 text-green-500" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                      {meeting.status === 'in_progress' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEndMeeting(meeting.id)}
                        >
                          <Square className="w-3 h-3 mr-1" />
                          End
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedMeeting(meeting);
                          setMeetingNotes({ notes: meeting.notes || '', action_items: '', decisions: '' });
                          setShowNotesModal(true);
                        }}
                      >
                        <FileText className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteMeeting(meeting.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Meeting Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Schedule Meeting
            </DialogTitle>
            <DialogDescription>
              Create a new safety meeting with Google Meet
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Meeting Title</Label>
              <Input
                placeholder="e.g., Weekly Safety Review"
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Meeting agenda and details..."
                value={newMeeting.description}
                onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={newMeeting.category} 
                  onValueChange={(v) => setNewMeeting({ ...newMeeting, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Meeting Type</Label>
                <Select 
                  value={newMeeting.meeting_type} 
                  onValueChange={(v) => setNewMeeting({ ...newMeeting, meeting_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="instant">Instant</SelectItem>
                    <SelectItem value="recurring">Recurring</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={newMeeting.scheduled_start}
                  onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_start: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={newMeeting.scheduled_end}
                  onChange={(e) => setNewMeeting({ ...newMeeting, scheduled_end: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Attendee Emails</Label>
              <Input
                placeholder="email1@example.com, email2@example.com"
                value={newMeeting.attendee_emails}
                onChange={(e) => setNewMeeting({ ...newMeeting, attendee_emails: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Separate multiple emails with commas</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={() => handleCreateMeeting(false)} disabled={!newMeeting.title}>
              <Video className="w-4 h-4 mr-2" />
              Create Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Meeting Notes
            </DialogTitle>
            <DialogDescription>
              {selectedMeeting?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Meeting Notes</Label>
              <Textarea
                placeholder="Key discussion points and takeaways..."
                value={meetingNotes.notes}
                onChange={(e) => setMeetingNotes({ ...meetingNotes, notes: e.target.value })}
                rows={5}
              />
            </div>

            <div>
              <Label>Action Items</Label>
              <Textarea
                placeholder="One action item per line..."
                value={meetingNotes.action_items}
                onChange={(e) => setMeetingNotes({ ...meetingNotes, action_items: e.target.value })}
                rows={3}
              />
            </div>

            <div>
              <Label>Decisions Made</Label>
              <Textarea
                placeholder="One decision per line..."
                value={meetingNotes.decisions}
                onChange={(e) => setMeetingNotes({ ...meetingNotes, decisions: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesModal(false)}>Cancel</Button>
            <Button onClick={handleSaveNotes}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingsPage;
